import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

// Auth translations
export const authTranslations = {
  en: {
    signup: {
      title: 'Create Your Account',
      subtitle: 'Join Mid2Bid and start discovering opportunities',
      step1Title: 'Company Information',
      step1Subtitle: 'Tell us about your company',
      step2Title: 'Your Information',
      step2Subtitle: 'Create your personal account',
      companyName: 'Company Name',
      companyNamePlaceholder: 'Enter your company name',
      companyWebsite: 'Company Website',
      companyWebsitePlaceholder: 'https://example.com',
      companyWebsiteHint: 'Format: https://example.com (or example.com / www.example.com)',
      noWebsiteToggle: "I don't have a valid website",
      noWebsiteHelper: 'No problem — you can register without a company website.',
      companyCountry: 'Company Country',
      selectCountry: 'Select a country',
      primaryCountry: 'Primary Country of Interest (GCC)',
      selectPrimaryCountry: 'Select a GCC country',
      primaryCountryHint: 'Choose the GCC country you primarily target. You can change this later.',
      yourName: 'Your Full Name',
      yourNamePlaceholder: 'Enter your full name',
      yourTitle: 'Job Title',
      yourTitlePlaceholder: 'e.g. Business Development Manager',
      email: 'Work Email',
      emailPlaceholder: 'you@company.com',
      password: 'Password',
      passwordPlaceholder: 'Create a strong password',
      confirmPassword: 'Confirm Password',
      confirmPasswordPlaceholder: 'Confirm your password',
      next: 'Next Step',
      back: 'Back',
      createAccount: 'Create Account',
      creating: 'Creating...',
      haveAccount: 'Already have an account?',
      login: 'Log in',
      passwordRequirements: 'At least 8 characters with uppercase, lowercase, and number',
      domainMismatch: 'Email domain must match company website domain',
      genericEmailError: 'Please use your company email address',
      countries: {
        SA: 'Saudi Arabia',
        AE: 'United Arab Emirates',
        OM: 'Oman',
        KW: 'Kuwait',
        BH: 'Bahrain',
        QA: 'Qatar'
      }
    },
    verify: {
      title: 'Verify Your Email',
      subtitle: 'Enter the 8-digit code sent to',
      codeLabel: 'Verification Code',
      codePlaceholder: 'Enter 8-digit code',
      verify: 'Verify Email',
      verifying: 'Verifying...',
      resend: 'Resend Code',
      resendIn: 'Resend in',
      seconds: 'seconds',
      expiresIn: 'Code expires in',
      minutes: 'minutes',
      hours: 'hours',
      resending: 'Sending a new code...',
      resendSent: 'A new code has been sent to',
      resendFailed: 'We could not send a new code. Please try again in a moment.',
      invalidCode: 'Invalid verification code',
      codeExpired: 'Code expired. Please request a new one.',
      success: 'Email verified successfully!',
      redirecting: 'Redirecting to login...'
    },
    login: {
      title: 'Welcome Back',
      subtitle: 'Log in to your Mid2Bid account',
      email: 'Email',
      emailPlaceholder: 'you@company.com',
      password: 'Password',
      passwordPlaceholder: 'Enter your password',
      rememberMe: 'Remember me',
      forgotPassword: 'Forgot password?',
      login: 'Log In',
      loggingIn: 'Logging in...',
      noAccount: "Don't have an account?",
      signup: 'Sign up',
      invalidCredentials: 'Invalid email or password',
      pendingVerification: 'Please verify your email first',
      reverifyRequired: 'Account requires re-verification'
    },
    forgotPassword: {
      title: 'Reset Password',
      subtitle: 'Enter your email to receive a reset code',
      enterCodeSubtitle: 'Enter the code and your new password',
      emailLabel: 'Email Address',
      emailPlaceholder: 'Enter your email',
      emailRequired: 'Email is required',
      invalidEmail: 'Please enter a valid email',
      codeLabel: 'Verification Code',
      codeRequired: 'Verification code is required',
      invalidCode: 'Invalid verification code',
      codeExpired: 'Code expired. Please request a new one.',
      tooManyAttempts: 'Too many attempts. Please request a new code.',
      newPasswordLabel: 'New Password',
      newPasswordPlaceholder: 'Enter new password',
      confirmPasswordLabel: 'Confirm Password',
      confirmPasswordPlaceholder: 'Confirm new password',
      passwordRequired: 'New password is required',
      passwordMinLength: 'Password must be at least 8 characters',
      passwordUppercase: 'Password must contain an uppercase letter',
      passwordLowercase: 'Password must contain a lowercase letter',
      passwordNumber: 'Password must contain a number',
      passwordMismatch: 'Passwords do not match',
      sendCode: 'Send Reset Code',
      sending: 'Sending...',
      codeSent: 'If an account exists, a reset code has been sent to your email',
      codeSentTo: 'We sent an 8-digit code to',
      errorSending: 'Failed to send reset code. Please try again.',
      resetPassword: 'Reset Password',
      resetting: 'Resetting...',
      resetSuccess: 'Password reset successfully!',
      resendCode: "Didn't receive the code? Request again",
      successTitle: 'Success!',
      successMessage: 'You can now log in with your new password.',
      goToLogin: 'Go to Login',
      backToLogin: 'Back to Login'
    },
    dashboard: {
      title: 'Dashboard',
      welcome: 'Welcome back',
      intelligence: 'Intelligence',
      news: 'News',
      tenders: 'Tenders',
      projects: 'Projects',
      companies: 'Companies',
      crm: 'CRM',
      opportunities: 'Opportunities',
      contacts: 'Contacts',
      reports: 'Reports',
      analytics: 'Analytics',
      alerts: 'Alerts',
      group: 'Team',
      referrals: 'Refer & Earn',
      referralsSection: 'Referrals',
      settings: 'Settings',
      profile: 'Profile',
      plans: 'Plans',
      billing: 'Billing',
      logout: 'Log Out',
      comingSoon: 'Coming Soon',
      featureInDevelopment: 'This feature is currently in development',
      admin: 'Admin',
      database: 'Database',
      users: 'Users Management',
      executive: 'Executive Dashboard',
      revenue: 'Revenue Analytics',
      accounts: 'Accounts',
      productUsage: 'Product Usage',
      marketing: 'Marketing',
      funnel: 'Funnel Analytics',
      dataOps: 'Data Operations',
      tenderSources: 'Tender Sources',
      marketResearch: 'Market Research',
      systemHealth: 'System Health',
      activityLogs: 'Activity Logs',
      adminSettings: 'Admin Settings',
      referralAdmin: 'Referral Program'
    }
  },
  ar: {
    signup: {
      title: 'إنشاء حسابك',
      subtitle: 'انضم إلى Mid2Bid وابدأ باكتشاف الفرص',
      step1Title: 'معلومات الشركة',
      step1Subtitle: 'أخبرنا عن شركتك',
      step2Title: 'معلوماتك',
      step2Subtitle: 'أنشئ حسابك الشخصي',
      companyName: 'اسم الشركة',
      companyNamePlaceholder: 'أدخل اسم شركتك',
      companyWebsite: 'موقع الشركة',
      companyWebsitePlaceholder: 'https://example.com',
      companyWebsiteHint: 'الصيغة: https://example.com (أو example.com / www.example.com)',
      noWebsiteToggle: 'ليس لدي موقع إلكتروني صالح',
      noWebsiteHelper: 'لا مشكلة — يمكنك التسجيل بدون موقع إلكتروني للشركة.',
      companyCountry: 'بلد الشركة',
      selectCountry: 'اختر البلد',
      primaryCountry: 'بلد الاهتمام الرئيسي (دول الخليج)',
      selectPrimaryCountry: 'اختر بلدًا خليجيًا',
      primaryCountryHint: 'اختر دولة الخليج التي تستهدفها أساسًا. يمكنك تغيير ذلك لاحقًا.',
      yourName: 'اسمك الكامل',
      yourNamePlaceholder: 'أدخل اسمك الكامل',
      yourTitle: 'المسمى الوظيفي',
      yourTitlePlaceholder: 'مثال: مدير تطوير الأعمال',
      email: 'البريد الإلكتروني للعمل',
      emailPlaceholder: 'you@company.com',
      password: 'كلمة المرور',
      passwordPlaceholder: 'أنشئ كلمة مرور قوية',
      confirmPassword: 'تأكيد كلمة المرور',
      confirmPasswordPlaceholder: 'أكد كلمة المرور',
      next: 'التالي',
      back: 'رجوع',
      createAccount: 'إنشاء الحساب',
      creating: 'جارٍ الإنشاء...',
      haveAccount: 'لديك حساب بالفعل؟',
      login: 'تسجيل الدخول',
      passwordRequirements: '8 أحرف على الأقل مع أحرف كبيرة وصغيرة ورقم',
      domainMismatch: 'يجب أن يتطابق نطاق البريد الإلكتروني مع نطاق موقع الشركة',
      genericEmailError: 'الرجاء استخدام بريدك الإلكتروني الخاص بالعمل',
      countries: {
        SA: 'المملكة العربية السعودية',
        AE: 'الإمارات العربية المتحدة',
        OM: 'عمان',
        KW: 'الكويت',
        BH: 'البحرين',
        QA: 'قطر'
      }
    },
    verify: {
      title: 'تحقق من بريدك الإلكتروني',
      subtitle: 'أدخل الرمز المكون من 8 أرقام المرسل إلى',
      codeLabel: 'رمز التحقق',
      codePlaceholder: 'أدخل الرمز المكون من 8 أرقام',
      verify: 'تحقق من البريد الإلكتروني',
      verifying: 'جارٍ التحقق...',
      resend: 'إعادة إرسال الرمز',
      resendIn: 'إعادة الإرسال في',
      seconds: 'ثانية',
      expiresIn: 'ينتهي الرمز في',
      minutes: 'دقائق',
      hours: 'ساعة',
      resending: 'جارٍ إرسال رمز جديد...',
      resendSent: 'تم إرسال رمز جديد إلى',
      resendFailed: 'لم نتمكن من إرسال رمز جديد. يرجى المحاولة بعد قليل.',
      invalidCode: 'رمز التحقق غير صالح',
      codeExpired: 'انتهت صلاحية الرمز. يرجى طلب رمز جديد.',
      success: 'تم التحقق من البريد الإلكتروني بنجاح!',
      redirecting: 'جارٍ التوجيه لتسجيل الدخول...'
    },
    login: {
      title: 'مرحباً بعودتك',
      subtitle: 'سجل الدخول إلى حسابك في Mid2Bid',
      email: 'البريد الإلكتروني',
      emailPlaceholder: 'you@company.com',
      password: 'كلمة المرور',
      passwordPlaceholder: 'أدخل كلمة المرور',
      rememberMe: 'تذكرني',
      forgotPassword: 'نسيت كلمة المرور؟',
      login: 'تسجيل الدخول',
      loggingIn: 'جارٍ تسجيل الدخول...',
      noAccount: 'ليس لديك حساب؟',
      signup: 'سجل الآن',
      invalidCredentials: 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
      pendingVerification: 'يرجى التحقق من بريدك الإلكتروني أولاً',
      reverifyRequired: 'الحساب يتطلب إعادة التحقق'
    },
    forgotPassword: {
      title: 'إعادة تعيين كلمة المرور',
      subtitle: 'أدخل بريدك الإلكتروني لتلقي رمز إعادة التعيين',
      enterCodeSubtitle: 'أدخل الرمز وكلمة المرور الجديدة',
      emailLabel: 'البريد الإلكتروني',
      emailPlaceholder: 'أدخل بريدك الإلكتروني',
      emailRequired: 'البريد الإلكتروني مطلوب',
      invalidEmail: 'يرجى إدخال بريد إلكتروني صالح',
      codeLabel: 'رمز التحقق',
      codeRequired: 'رمز التحقق مطلوب',
      invalidCode: 'رمز التحقق غير صالح',
      codeExpired: 'انتهت صلاحية الرمز. يرجى طلب رمز جديد.',
      tooManyAttempts: 'محاولات كثيرة. يرجى طلب رمز جديد.',
      newPasswordLabel: 'كلمة المرور الجديدة',
      newPasswordPlaceholder: 'أدخل كلمة المرور الجديدة',
      confirmPasswordLabel: 'تأكيد كلمة المرور',
      confirmPasswordPlaceholder: 'أكد كلمة المرور الجديدة',
      passwordRequired: 'كلمة المرور الجديدة مطلوبة',
      passwordMinLength: 'يجب أن تكون كلمة المرور 8 أحرف على الأقل',
      passwordUppercase: 'يجب أن تحتوي كلمة المرور على حرف كبير',
      passwordLowercase: 'يجب أن تحتوي كلمة المرور على حرف صغير',
      passwordNumber: 'يجب أن تحتوي كلمة المرور على رقم',
      passwordMismatch: 'كلمات المرور غير متطابقة',
      sendCode: 'إرسال رمز إعادة التعيين',
      sending: 'جارٍ الإرسال...',
      codeSent: 'إذا كان الحساب موجوداً، تم إرسال رمز إعادة التعيين إلى بريدك الإلكتروني',
      codeSentTo: 'أرسلنا رمزاً مكوناً من 8 أرقام إلى',
      errorSending: 'فشل في إرسال رمز إعادة التعيين. يرجى المحاولة مرة أخرى.',
      resetPassword: 'إعادة تعيين كلمة المرور',
      resetting: 'جارٍ إعادة التعيين...',
      resetSuccess: 'تم إعادة تعيين كلمة المرور بنجاح!',
      resendCode: 'لم تستلم الرمز؟ اطلب مرة أخرى',
      successTitle: 'تم بنجاح!',
      successMessage: 'يمكنك الآن تسجيل الدخول بكلمة المرور الجديدة.',
      goToLogin: 'الذهاب لتسجيل الدخول',
      backToLogin: 'العودة لتسجيل الدخول'
    },
    dashboard: {
      title: 'لوحة التحكم',
      welcome: 'مرحباً بعودتك',
      intelligence: 'المعلومات',
      news: 'الأخبار',
      tenders: 'المناقصات',
      projects: 'المشاريع',
      companies: 'الشركات',
      crm: 'إدارة العملاء',
      opportunities: 'الفرص',
      contacts: 'جهات الاتصال',
      reports: 'التقارير',
      analytics: 'التحليلات',
      alerts: 'التنبيهات',
      group: 'الفريق',
      referrals: 'أحِل واربح',
      referralsSection: 'الإحالات',
      settings: 'الإعدادات',
      profile: 'الملف الشخصي',
      plans: 'الخطط',
      billing: 'الفواتير',
      logout: 'تسجيل الخروج',
      comingSoon: 'قريباً',
      featureInDevelopment: 'هذه الميزة قيد التطوير حالياً',
      admin: 'الإدارة',
      database: 'قاعدة البيانات',
      users: 'إدارة المستخدمين',
      executive: 'لوحة التحكم التنفيذية',
      revenue: 'تحليلات الإيرادات',
      accounts: 'الحسابات',
      productUsage: 'استخدام المنتج',
      marketing: 'التسويق',
      funnel: 'تحليلات القمع',
      dataOps: 'عمليات البيانات',
      tenderSources: 'مصادر المناقصات',
      marketResearch: 'أبحاث السوق',
      systemHealth: 'صحة النظام',
      activityLogs: 'سجلات النشاط',
      adminSettings: 'إعدادات الإدارة',
      referralAdmin: 'برنامج الإحالة'
    }
  }
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  const isRTL = language === 'ar';

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'ar' : 'en';
    setLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  const t = authTranslations[language];

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRTL]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
